import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import AppShell from '../shell/AppShell';
import { UserContext } from '../../UserContext';
import dataService from '../../services/dataService';
import { useAuthModal } from '../../features/auth/AuthModalContext';
import { useI18n } from '../../features/i18n';
import { useDocumentTitle } from '../shell/DocumentTitle';
import { Skeleton, SkeletonRow } from '../shell/Skeleton';
import '../../features/brand/cwm-tokens.css';

/**
 * /unified/restaurant/:id — deep-linkable restaurant detail page. Cover hero
 * with gradient + dot pattern, rating + cuisine + distance chip, action bar
 * (Chat / Share Safety Signal / Ask the table), menu highlights grouped by
 * category, recent safety signals for this restaurant, hours + address.
 */

const EASE = [0.22, 1, 0.36, 1];

export default function UnifiedRestaurantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userId } = useContext(UserContext);
  const { openAuth } = useAuthModal();
  const { t } = useI18n();
  // The /restaurant/:id endpoint returns a wrapper object:
  //   { restaurant: {...}, menus: [...], documents: [...] }
  // Split it apart on load so the render code below can read the
  // restaurant fields directly (previously we stored the whole wrapper,
  // which is why the hero was rendering blank: `restaurant.name` was
  // undefined even though `data.restaurant.name` was "CECCONI'S").
  const [restaurant, setRestaurant] = useState(null);
  const [menusData, setMenusData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Keep the browser tab title aligned with whichever restaurant we're
  // viewing so bookmarks and tab-flipping make sense.
  useDocumentTitle(restaurant?.name || null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await dataService.getRestaurant(id);
        if (cancelled) return;
        // Unwrap. Backend may return either the wrapper shape or a flat
        // row (older endpoints); treat both gracefully.
        const row   = data?.restaurant || data || null;
        const menus = Array.isArray(data?.menus) ? data.menus : [];
        setRestaurant(row);
        setMenusData(menus);
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Failed to load restaurant.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  if (loading) return <AppShell backdrop><Loader t={t} /></AppShell>;
  if (!restaurant) return <AppShell backdrop><NotFound t={t} onBack={() => navigate('/unified/discover')} /></AppShell>;

  const cuisine = restaurant.cuisine_type || restaurant.cuisine || '';
  const rating = restaurant.display_rating || restaurant.rating || restaurant.google_rating || null;
  const reviews = restaurant.display_review_count || restaurant.google_user_ratings_total || 0;
  // Fall back to Google-enriched fields when the primary ones are empty.
  // A lot of rows only carry address/phone via their Google Place lookup.
  const address = restaurant.address || restaurant.google_address || '';
  const website = restaurant.website || restaurant.google_website || '';
  const phone = restaurant.phone || restaurant.google_phone || '';
  const priceRange = restaurant.price_range || '';
  const dietary = restaurant.dietary_display_tags || restaurant.card_tags || [];
  // Hero photo — the /listRestaurants payload doesn't carry a direct
  // photo URL, but the backend's Google-Places photo proxy serves the
  // first photo by index. Same source the discovery cards use.
  const photo = restaurant.photo_url
    || restaurant.photo
    || (restaurant.id ? `/api/restaurant/${restaurant.id}/photo/0` : null);

  // Flatten menu items from any/all menus for the "highlights" section.
  // `menusData` is the unwrapped array from the API response; each
  // entry's menu_data.menus holds categories + items.
  const menus = menusData.flatMap((m) => m.menu_data?.menus || []);
  const categories = menus.flatMap((m) => (m.categories || []).map((c) => ({ ...c, menuName: m.name })));

  const onChat = () => {
    if (!userId) { openAuth('signin'); return; }
    navigate('/unified/chat', { state: { restaurantId: restaurant.id, restaurantName: restaurant.name } });
  };

  return (
    <AppShell>
      {/* Cover hero */}
      <motion.section
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}
        style={{
          position: 'relative', overflow: 'hidden',
          minHeight: 280,
          backgroundImage: photo ? `linear-gradient(180deg, rgba(10,37,64,0.28), rgba(10,37,64,0.72)), url(${photo})` : 'linear-gradient(160deg, #BCE7FF 0%, #5BC2FF 45%, #1E9BFF 100%)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          color: '#fff',
        }}>
        {!photo && (
          <div aria-hidden style={{
            position: 'absolute', inset: 0, pointerEvents: 'none',
            background: 'radial-gradient(circle, rgba(255,255,255,0.22) 0, rgba(255,255,255,0.22) 1.5px, transparent 1.6px)',
            backgroundSize: '22px 22px', opacity: 0.3,
          }}/>
        )}
        <div style={{ position: 'relative', maxWidth: 980, margin: '0 auto', padding: '32px 20px 24px' }}>
          <button type="button" onClick={() => navigate(-1)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '6px 14px', borderRadius: 999,
                    background: 'rgba(255,255,255,0.18)', color: '#fff',
                    border: '1.5px solid rgba(255,255,255,0.3)',
                    fontFamily: 'var(--cwm-font)', fontSize: 12, fontWeight: 800,
                    cursor: 'pointer', backdropFilter: 'blur(6px)', marginBottom: 22,
                  }}>
            <BackIcon size={12} /> Back
          </button>
          {cuisine && (
            <div style={{ fontSize: 10.5, fontWeight: 900, letterSpacing: '0.14em', textTransform: 'uppercase', opacity: 0.9, marginBottom: 8 }}>
              {cuisine}
            </div>
          )}
          <h1 style={{ fontSize: 40, fontWeight: 900, letterSpacing: '-0.028em', margin: 0, lineHeight: 1.05, textShadow: '0 4px 16px rgba(10,37,64,0.25)' }}>
            {restaurant.name}
          </h1>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 14, alignItems: 'center', fontSize: 13, fontWeight: 800 }}>
            {rating && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 999, background: 'rgba(255,255,255,0.22)', backdropFilter: 'blur(6px)' }}>
                <span style={{ color: '#F5A623', fontSize: 14 }}>★</span>
                {rating.toFixed ? rating.toFixed(1) : rating} <span style={{ opacity: 0.8, fontWeight: 600 }}>({reviews})</span>
              </span>
            )}
            {priceRange && (
              <span style={{ padding: '4px 10px', borderRadius: 999, background: 'rgba(255,255,255,0.22)', backdropFilter: 'blur(6px)' }}>
                {priceRange}
              </span>
            )}
            {address && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, opacity: 0.94 }}>
                <PinIcon size={12} /> {address}
              </span>
            )}
          </div>
        </div>
      </motion.section>

      <div style={{ maxWidth: 980, margin: '0 auto', padding: '24px 20px 80px' }}>

        {error && (
          <div style={{ padding: '12px 16px', borderRadius: 14, marginBottom: 18, background: 'var(--cwm-red-light)', color: 'var(--cwm-red-text)', fontWeight: 700, fontSize: 14 }}>
            {error}
          </div>
        )}

        {/* Action bar */}
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.46, ease: EASE }}
          style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, marginBottom: 24 }}
        >
          <ActionButton primary onClick={onChat} icon="chat" label={t('restaurant.chat_cta')} />
          <ActionButton onClick={() => navigate('/unified/safety-signals')} icon="shield" label={t('restaurant.safety_signal_cta')} />
          <ActionButton onClick={() => navigate('/unified/table-questions')} icon="question" label={t('restaurant.ask_table_cta')} />
          <ActionButton onClick={() => navigate('/unified/add-review', { state: { restaurantId: restaurant.id, restaurantName: restaurant.name } })} icon="pen" label={t('restaurant.write_review')} />
        </motion.div>

        {/* Dietary tags */}
        {dietary.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <div className="cwm-eyebrow" style={{ marginBottom: 8 }}>{t('restaurant.dietary_eyebrow')}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {dietary.map((tag, i) => (
                <span key={i} style={{
                  padding: '5px 12px', borderRadius: 999,
                  background: 'var(--cwm-green-light)', color: 'var(--cwm-green-text)',
                  fontSize: 12, fontWeight: 800, border: '1px solid #A6E8C2',
                }}>✓ {tag}</span>
              ))}
            </div>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.5fr) minmax(280px, 1fr)', gap: 18 }} className="cwm-rest-grid">
          <style>{`@media (max-width: 800px) { .cwm-rest-grid { grid-template-columns: 1fr !important; } }`}</style>

          {/* Menu highlights */}
          <section>
            <div className="cwm-eyebrow" style={{ marginBottom: 10 }}>{t('restaurant.menu_highlights')}</div>
            {categories.length === 0 ? (
              <div className="cwm-sheet" style={{ padding: 24, textAlign: 'center', color: 'var(--cwm-gray-500)', fontSize: 13, fontWeight: 600 }}>
                {t('restaurant.no_menu')}
              </div>
            ) : (
              <div style={{ display: 'grid', gap: 16 }}>
                {categories.slice(0, 4).map((cat, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.36, delay: i * 0.06, ease: EASE }}
                  >
                    <div style={{ fontSize: 13, fontWeight: 900, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--cwm-blue-deep)', marginBottom: 8 }}>
                      {cat.name}
                    </div>
                    <div style={{ display: 'grid', gap: 8 }}>
                      {(cat.items || []).filter((it) => it.available !== false).slice(0, 4).map((it, j) => (
                        <div key={j} className="cwm-sheet"
                             onClick={() => navigate(`/unified/dish/${restaurant.id || id}/${it.id}?category=${cat.id}`)}
                             style={{ padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, cursor: 'pointer', transition: 'all 160ms ease' }}
                             onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-1px)'}
                             onMouseLeave={(e) => e.currentTarget.style.transform = ''}>

                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 14, fontWeight: 900, color: 'var(--cwm-ink)' }}>{it.name}</div>
                            {it.description && (
                              <div style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--cwm-gray-700)', marginTop: 2, lineHeight: 1.5 }}>
                                {it.description}
                              </div>
                            )}
                            {(it.allergens?.length > 0 || it.dietary_tags?.length > 0) && (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                                {(it.dietary_tags || []).map((d) => (
                                  <span key={d} style={{ padding: '2px 8px', borderRadius: 999, background: 'var(--cwm-green-light)', color: 'var(--cwm-green-text)', fontSize: 10, fontWeight: 800 }}>✓ {d}</span>
                                ))}
                                {(it.allergens || []).map((a) => (
                                  <span key={a} style={{ padding: '2px 8px', borderRadius: 999, background: 'var(--cwm-red-light)', color: 'var(--cwm-red-text)', fontSize: 10, fontWeight: 800 }}>⚠ {a}</span>
                                ))}
                              </div>
                            )}
                          </div>
                          {typeof it.price === 'number' && (
                            <div style={{ fontSize: 14, fontWeight: 900, color: 'var(--cwm-ink)', whiteSpace: 'nowrap' }}>
                              ${(it.price >= 100 ? it.price / 100 : it.price).toFixed(2)}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </motion.div>
                ))}
                {categories.length > 4 && (
                  <button className="cwm-btn-secondary" onClick={onChat} style={{ width: 'fit-content', marginTop: 4 }}>
                    {t('restaurant.see_full_menu')} <ArrowIcon size={12} />
                  </button>
                )}
              </div>
            )}

            {/* Reviews */}
            <ReviewsSection
              t={t}
              restaurantId={restaurant.id}
              restaurantName={restaurant.name}
              rating={rating}
              totalReviews={reviews}
              onWriteReview={() => navigate('/unified/add-review', { state: { restaurantId: restaurant.id, restaurantName: restaurant.name } })}
            />
          </section>

          {/* Sidebar: tight contact card. The old version also had a
              "Safety Signals" block with a "View signals" button, which
              was a duplicate of the "Share safety signal" action already
              in the top action bar above. Removed to avoid double-
              presenting the same affordance. */}
          <aside style={{ display: 'grid', gap: 12 }}>
            <div className="cwm-sheet" style={{ padding: '12px 14px' }}>
              <div className="cwm-eyebrow" style={{ marginBottom: 8 }}>{t('restaurant.contact_eyebrow')}</div>
              {phone && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <PhoneIcon size={13} color="var(--cwm-blue-deep)" />
                  <a href={`tel:${phone}`} style={{ fontSize: 13, fontWeight: 800, color: 'var(--cwm-blue-deep)', textDecoration: 'none' }}>{phone}</a>
                </div>
              )}
              {website && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <GlobeIcon size={13} color="var(--cwm-blue-deep)" />
                  {/* Show a clean hyperlink label, not the raw tracking-
                      query URL. Rendering the full Google-UTM'd URL was
                      both ugly and wrapped the sidebar. */}
                  <a href={website} target="_blank" rel="noreferrer"
                     style={{ fontSize: 13, fontWeight: 800, color: 'var(--cwm-blue-deep)', textDecoration: 'underline', textUnderlineOffset: 2 }}>
                    Visit website
                  </a>
                </div>
              )}
              {address && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <PinIcon size={13} color="var(--cwm-blue-deep)" />
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--cwm-gray-700)', lineHeight: 1.4 }}>{address}</div>
                </div>
              )}
              {!phone && !website && !address && (
                <div style={{ fontSize: 12.5, color: 'var(--cwm-gray-500)', fontWeight: 600 }}>{t('restaurant.contact_none')}</div>
              )}
            </div>
          </aside>
        </div>
      </div>
    </AppShell>
  );
}

// ============================================================
// REVIEWS
// ============================================================
//
// Liability-safe: we ONLY render reviews we can actually attribute
// (real first-party reviews from `/reviews/:user/:restaurant` or the
// forthcoming Google Places passthrough endpoint). The previous
// version seeded the UI with a hardcoded DEMO_REVIEWS_POOL — fake
// quotes attributed to made-up diners — which is unacceptable on an
// allergy-safety product. If we have no real reviews yet, we show
// an empty state + a "Write the first review" CTA instead.

function ReviewsSection({ t, restaurantId, restaurantName, rating, totalReviews, onWriteReview }) {
  const [liveReviews, setLiveReviews] = React.useState(null);  // null = not fetched yet
  React.useEffect(() => {
    if (!restaurantId) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await dataService.listRestaurantReviews(restaurantId);
        if (!cancelled) setLiveReviews(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setLiveReviews([]);
      }
    })();
    return () => { cancelled = true; };
  }, [restaurantId]);

  // Map the live Review row into the card shape used below.
  const fromLive = (r) => {
    const name = r.user_name || 'Diner';
    const initials = name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();
    const daysAgo = r.datetime
      ? Math.max(0, Math.floor((Date.now() - new Date(r.datetime).getTime()) / 86400000))
      : 0;
    return {
      name,
      initials,
      rating: r.rating,
      safety: r.safety_rating ?? null,  // only when the reviewer gave one
      daysAgo,
      tags: r.allergen_tags || [],
      text: r.review_text,
    };
  };
  const shown = (liveReviews || []).map(fromLive).slice(0, 4);
  const hasReviews = shown.length > 0;

  // Aggregate row uses only the real rating + count passed in from the
  // Google-enriched row — no synthetic averages when we have nothing.
  const avgRating = rating ? (rating.toFixed ? rating.toFixed(1) : rating) : null;
  const safetyRated = shown.filter((r) => r.safety != null);
  const avgSafety = safetyRated.length
    ? (safetyRated.reduce((n, r) => n + r.safety, 0) / safetyRated.length).toFixed(1)
    : null;
  const count = totalReviews || (liveReviews?.length ?? 0);

  return (
    <div style={{ marginTop: 28 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 10, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div className="cwm-eyebrow">{t('restaurant.reviews_eyebrow')}</div>
          <div style={{ fontSize: 18, fontWeight: 900, color: 'var(--cwm-ink)', marginTop: 2, letterSpacing: '-0.01em' }}>
            {t('restaurant.reviews_title')}
          </div>
        </div>
        <button type="button" onClick={onWriteReview} className="cwm-btn-secondary" style={{ padding: '8px 16px', fontSize: 12.5 }}>
          <PenIcon size={12} /> {t('restaurant.write_review')}
        </button>
      </div>

      {/* Summary row — rating only renders when we have a real Google
          aggregate; safety-average only when we have real first-party
          reviews. Otherwise we show an empty-state card (below). */}
      {avgRating && (
        <div className="cwm-sheet" style={{
          padding: 16,
          display: 'grid',
          gridTemplateColumns: hasReviews ? 'auto 1fr auto' : 'auto 1fr',
          gap: 16, alignItems: 'center',
          background: 'linear-gradient(160deg, #fff, var(--cwm-blue-pale))',
          marginBottom: 12,
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 30, fontWeight: 900, color: 'var(--cwm-blue-deep)', letterSpacing: '-0.02em', lineHeight: 1 }}>
              {avgRating}
            </div>
            <StarRow value={Number(avgRating)} size={10} />
            <div style={{ fontSize: 10.5, fontWeight: 800, color: 'var(--cwm-gray-500)', marginTop: 3, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              {t('restaurant.reviews_count', { count })}
            </div>
          </div>
          {hasReviews ? (
            <>
              <div style={{ borderLeft: '1px solid var(--cwm-gray-100)', paddingLeft: 16 }}>
                <div className="cwm-eyebrow" style={{ color: 'var(--cwm-green-text)', marginBottom: 4 }}>{t('restaurant.reviews_safety_eyebrow')}</div>
                <div style={{ fontSize: 22, fontWeight: 900, color: 'var(--cwm-green-text)', letterSpacing: '-0.01em', lineHeight: 1 }}>{avgSafety}/5</div>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--cwm-gray-700)', marginTop: 3, lineHeight: 1.4 }}>
                  {t('restaurant.reviews_safety_hint')}
                </div>
              </div>
              <div style={{
                width: 44, height: 44, borderRadius: 14,
                background: 'var(--cwm-green-light)', color: 'var(--cwm-green-text)',
                display: 'grid', placeItems: 'center',
                border: '1.5px solid #A6E8C2',
              }}>
                <ShieldIcon size={20} />
              </div>
            </>
          ) : (
            <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--cwm-gray-700)', lineHeight: 1.45, paddingLeft: 16, borderLeft: '1px solid var(--cwm-gray-100)' }}>
              Aggregate rating is sourced from Google. Allergy-specific
              diner reviews will appear here as the community writes them.
            </div>
          )}
        </div>
      )}

      {/* Empty state — shown only when we have no real first-party
          reviews. We intentionally do NOT fill this with sample
          quotes; attributing fake words to made-up people on an
          allergy-safety product is a liability we shouldn't ship. */}
      {!hasReviews && (
        <div className="cwm-sheet" style={{
          padding: '20px 18px',
          textAlign: 'center',
          background: '#fff',
          border: '1px dashed var(--cwm-gray-200)',
        }}>
          <div style={{ fontSize: 14, fontWeight: 900, color: 'var(--cwm-ink)', marginBottom: 4 }}>
            No diner reviews yet
          </div>
          <div style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--cwm-gray-700)', lineHeight: 1.5, marginBottom: 12 }}>
            Be the first to share how {restaurantName || 'this spot'} handled your allergies or diet.
          </div>
          <button type="button" onClick={onWriteReview} className="cwm-btn-primary" style={{ padding: '8px 18px', fontSize: 13 }}>
            <PenIcon size={12} /> {t('restaurant.write_review')}
          </button>
        </div>
      )}

      {/* Review cards — only renders when we have real data */}
      <div style={{ display: 'grid', gap: 10 }}>
        {shown.map((r, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: i * 0.06, ease: EASE }}
            className="cwm-sheet" style={{ padding: 16 }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <div style={{
                width: 34, height: 34, borderRadius: 12,
                background: avatarGradient(r.initials),
                color: '#fff', display: 'grid', placeItems: 'center',
                fontSize: 12, fontWeight: 900, flexShrink: 0,
              }}>
                {r.initials}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 900, color: 'var(--cwm-ink)' }}>{r.name}</div>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--cwm-gray-500)' }}>
                  {relativeTime(t, r.daysAgo)}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <StarRow value={r.rating} size={11} />
                {r.safety != null && (
                <div style={{ fontSize: 10, fontWeight: 800, color: 'var(--cwm-green-text)', marginTop: 2, letterSpacing: '0.05em', textTransform: 'uppercase', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                  <ShieldIcon size={9} /> {t('restaurant.reviews_safety_eyebrow')} {r.safety}/5
                </div>
                )}
              </div>
            </div>
            <p style={{ margin: 0, fontSize: 13.5, fontWeight: 500, color: 'var(--cwm-gray-700)', lineHeight: 1.55 }}>
              "{r.text}"
            </p>
            {r.tags?.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 10 }}>
                {r.tags.map((tag) => (
                  <span key={tag} style={{
                    padding: '2px 9px', borderRadius: 999,
                    background: 'var(--cwm-red-light)', color: 'var(--cwm-red-text)',
                    fontSize: 10, fontWeight: 800,
                  }}>⚠ {tag}</span>
                ))}
              </div>
            )}
          </motion.div>
        ))}
      </div>

      {count > shown.length && (
        <div style={{ marginTop: 12, textAlign: 'center' }}>
          <button className="cwm-btn-secondary" style={{ padding: '8px 18px', fontSize: 12.5 }}>
            {t('restaurant.reviews_see_all', { count })} <ArrowIcon size={12} />
          </button>
        </div>
      )}
    </div>
  );
}

function StarRow({ value = 0, size = 12 }) {
  return (
    <div style={{ display: 'inline-flex', gap: 1 }}>
      {[1, 2, 3, 4, 5].map((i) => (
        <svg key={i} width={size} height={size} viewBox="0 0 24 24" fill={i <= value ? '#F6B93B' : 'none'} stroke={i <= value ? '#F6B93B' : 'var(--cwm-gray-300)'} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2l3.1 6.3 6.9 1-5 4.9 1.2 6.8L12 18l-6.2 3 1.2-6.8-5-4.9 6.9-1z"/>
        </svg>
      ))}
    </div>
  );
}

function avatarGradient(initials) {
  const hue = Array.from(initials || 'XX').reduce((a, c) => a + c.charCodeAt(0), 0) * 47 % 360;
  return `linear-gradient(160deg, hsl(${hue} 70% 60%), hsl(${(hue + 40) % 360} 65% 45%))`;
}

function relativeTime(t, days) {
  if (days < 1) return t('restaurant.time_today');
  if (days === 1) return t('restaurant.time_yesterday');
  if (days < 7) return t('restaurant.time_days_ago', { n: days });
  if (days < 14) return t('restaurant.time_week_ago');
  if (days < 30) return t('restaurant.time_weeks_ago', { n: Math.round(days / 7) });
  if (days < 60) return t('restaurant.time_month_ago');
  return t('restaurant.time_months_ago', { n: Math.round(days / 30) });
}

function PenIcon({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4v16h16v-7M18 2l4 4-10 10h-4v-4z"/></svg>; }

function ActionButton({ primary, onClick, icon, label }) {
  const paths = {
    chat:     'M21 12a9 9 0 01-4.5 7.8L17 22l-4-2a9 9 0 119-8z',
    shield:   'M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z',
    question: 'M8 10h.01M12 10h.01M16 10h.01M21 12a9 9 0 01-4.5 7.8L17 22l-4-2a9 9 0 119-8z',
    pen:      'M11 4H4v16h16v-7M18 2l4 4-10 10h-4v-4z',
  };
  return (
    <motion.button type="button" onClick={onClick}
      whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }}
      style={{
        padding: '14px 18px', borderRadius: 16,
        background: primary ? 'linear-gradient(90deg, #1E9BFF, #0F7FE8)' : '#fff',
        color: primary ? '#fff' : 'var(--cwm-blue-deep)',
        border: primary ? 'none' : '1.5px solid var(--cwm-blue-light)',
        fontFamily: 'var(--cwm-font)', fontSize: 13.5, fontWeight: 900,
        cursor: 'pointer', transition: 'all 160ms ease',
        boxShadow: primary ? '0 8px 20px rgba(15,127,232,0.35)' : '0 4px 12px rgba(13,71,161,0.08)',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
      }}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d={paths[icon] || paths.chat}/>
      </svg>
      {label}
    </motion.button>
  );
}

function Loader({ t }) {
  // Skeleton preview of the page the user is about to see: tall hero
  // band, action row pills, then two columns (menu + sidebar). Feels
  // snappier than a raw spinner because the layout doesn't shift in.
  return (
    <>
      <div style={{ height: 260, background: 'linear-gradient(160deg, var(--cwm-blue-pale) 0%, #D6EDFF 100%)' }}/>
      <div style={{ maxWidth: 980, margin: '-36px auto 0', padding: '0 20px 40px' }}>
        <div className="cwm-sheet" style={{ padding: 20, marginBottom: 16 }}>
          <Skeleton h={20} w={240} mb={8} />
          <Skeleton h={14} w={360} mb={16} />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
            {[0,1,2,3].map((i) => <Skeleton key={i} h={42} radius={14} />)}
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.5fr) minmax(280px, 1fr)', gap: 18 }}>
          <section>
            <Skeleton h={12} w={120} mb={10} />
            <div style={{ display: 'grid', gap: 10 }}>
              {[0,1,2].map((i) => (
                <div key={i} className="cwm-sheet" style={{ padding: 14 }}>
                  <Skeleton h={16} w="40%" mb={6} />
                  <SkeletonRow lines={2} h={11} />
                </div>
              ))}
            </div>
          </section>
          <aside>
            <div className="cwm-sheet" style={{ padding: 18, marginBottom: 12 }}>
              <Skeleton h={12} w={80} mb={10} />
              <SkeletonRow lines={3} h={12} />
            </div>
            <div className="cwm-sheet" style={{ padding: 18 }}>
              <Skeleton h={12} w={110} mb={10} />
              <Skeleton h={42} radius={14} />
            </div>
          </aside>
        </div>
        <span style={{ position: 'absolute', left: -9999 }}>{t('restaurant.loading')}</span>
      </div>
    </>
  );
}

function NotFound({ t, onBack }) {
  return (
    <div style={{ maxWidth: 500, margin: '0 auto', padding: '80px 20px', textAlign: 'center' }}>
      <h1 style={{ fontSize: 26, fontWeight: 900, color: 'var(--cwm-ink)', margin: '0 0 8px' }}>{t('restaurant.not_found_title')}</h1>
      <p style={{ fontSize: 14, color: 'var(--cwm-gray-700)', marginBottom: 18 }}>{t('restaurant.not_found_body')}</p>
      <button className="cwm-btn" onClick={onBack}>{t('restaurant.not_found_cta')}</button>
    </div>
  );
}

function BackIcon({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>; }
function PinIcon({ size = 12, color = 'currentColor' }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>; }
function PhoneIcon({ size = 14, color = 'currentColor' }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.9v3a2 2 0 01-2.2 2 20 20 0 01-8.6-3.1 20 20 0 01-6-6 20 20 0 01-3.1-8.6A2 2 0 014 2h3a2 2 0 012 1.7 13 13 0 00.7 2.8 2 2 0 01-.4 2.1L8 10a16 16 0 006 6l1.4-1.4a2 2 0 012.1-.4 13 13 0 002.8.7A2 2 0 0122 16.9z"/></svg>; }
function GlobeIcon({ size = 14, color = 'currentColor' }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3a14 14 0 010 18M12 3a14 14 0 000 18"/></svg>; }
function ShieldIcon({ size = 13 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z"/></svg>; }
function ArrowIcon({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>; }
